"use client";

import React from "react";
import { Activity, ArrowUpRight, Baby, Brain, Clock, HeartHandshake, Salad, Scissors, ShieldAlert, ShieldCheck, ShoppingBag, Sparkles, Stethoscope } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
interface TopicItem {
  id: string;
  title: string;
  description: string;
  category: string;
  to: string;
  iconName: string;
  articleCount: string;
}
const TOPICS: TopicItem[] = [{
  id: "health",
  title: "Cat Health",
  description: "Clinical symptoms, preventative treatments, vaccinations, and veterinary care.",
  category: "Clinical Care",
  to: "/PetHealthPage",
  iconName: "activity",
  articleCount: "48 articles"
}, {
  id: "nutrition",
  title: "Cat Nutrition",
  description: "Wet versus dry feeding, life-stage diets, moisture needs, and ingredient analysis.",
  category: "Dietary Science",
  to: "/NutritionPage",
  iconName: "salad",
  articleCount: "36 articles"
}, {
  id: "behavior",
  title: "Cat Behavior",
  description: "Litter box habits, feline communication, scratching solutions, and enrichment.",
  category: "Feline Psychology",
  to: "/BehaviorTrainingPage",
  iconName: "brain",
  articleCount: "42 articles"
}, {
  id: "breeds",
  title: "Cat Breeds",
  description: "Detailed breed profiles, physical temperaments, grooming needs, and health risks.",
  category: "Breed Index",
  to: "/BreedsPage",
  iconName: "sparkles",
  articleCount: "30 profiles"
}, {
  id: "kittens",
  title: "Kittens",
  description: "Early development, socialization windows, weaning nutrition, and initial vaccines.",
  category: "Life Stages",
  to: "/CatsPage",
  iconName: "baby",
  articleCount: "24 guides"
}, {
  id: "senior-cats",
  title: "Senior Cats",
  description: "Arthritis management, kidney monitoring, senior comfort, and routine adaptations.",
  category: "Life Stages",
  to: "/CatsPage",
  iconName: "clock",
  articleCount: "19 guides"
}, {
  id: "grooming",
  title: "Grooming",
  description: "Coat care, shedding control, nail trimming techniques, and hairball prevention.",
  category: "Hygiene & Coat",
  to: "/CatsPage",
  iconName: "scissors",
  articleCount: "15 articles"
}, {
  id: "dental-care",
  title: "Dental Care",
  description: "Feline periodontal health, toothbrushing guidance, and dental scaling insights.",
  category: "Preventative",
  to: "/PetHealthPage",
  iconName: "stethoscope",
  articleCount: "12 guides"
}, {
  id: "cat-safety",
  title: "Cat Safety",
  description: "Toxic household plants, safe indoor environments, and feline first-aid fundamentals.",
  category: "Toxicology & Safety",
  to: "/PetHealthPage",
  iconName: "shieldAlert",
  articleCount: "22 protocols"
}, {
  id: "cat-products",
  title: "Cat Products",
  description: "Vet-tested litter formulas, carriers, scratchers, microchip feeders, and monitors.",
  category: "Tested Gear",
  to: "/ProductsReviewsPage",
  iconName: "shoppingBag",
  articleCount: "38 reviews"
}, {
  id: "cat-insurance",
  title: "Cat Insurance",
  description: "Policy comparison parameters, hereditary condition coverage, and claim workflows.",
  category: "Financial Care",
  to: "/PetHealthPage",
  iconName: "heartHandshake",
  articleCount: "14 reviews"
}];
function renderTopicIcon(iconName: string) {
  switch (iconName) {
    case "activity":
      return <Activity className="size-5 text-accent" />;
    case "salad":
      return <Salad className="size-5 text-accent" />;
    case "brain":
      return <Brain className="size-5 text-accent" />;
    case "sparkles":
      return <Sparkles className="size-5 text-accent" />;
    case "baby":
      return <Baby className="size-5 text-accent" />;
    case "clock":
      return <Clock className="size-5 text-accent" />;
    case "scissors":
      return <Scissors className="size-5 text-accent" />;
    case "stethoscope":
      return <Stethoscope className="size-5 text-accent" />;
    case "shieldAlert":
      return <ShieldAlert className="size-5 text-accent" />;
    case "shoppingBag":
      return <ShoppingBag className="size-5 text-accent" />;
    case "heartHandshake":
      return <HeartHandshake className="size-5 text-accent" />;
    default:
      return <Activity className="size-5 text-accent" />;
  }
}
export default function Topics() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.05
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 14
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.01 : 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="intro-topics" data-nav="light" className="relative bg-background py-14 md:py-20 lg:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block with Clinical Governance Tag */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-muted border border-border text-muted-foreground text-xs font-semibold tracking-wider uppercase mb-4">
            <ShieldCheck className="size-3.5 text-accent" />
            <span>Veterinary-Reviewed Feline Index</span>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-tight mb-4">
            Cat Care &amp; Wellness
          </h1>

          <p className="text-lg md:text-xl text-muted-foreground font-normal leading-relaxed">
            Trusted answers for every stage of your cat's life.
          </p>
        </div>

        {/* 11-Topic Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-60px"
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
          {TOPICS.map((topic, i) => <motion.div key={topic.id} data-index={i} variants={cardVariants} className={i === 0 ? "sm:col-span-2 lg:col-span-2 xl:col-span-2" : ""}>
              <Link to={topic.to} className="group block h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-xl">
                <Card className="h-full border border-border bg-card/80 hover:bg-card transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-1 hover:shadow-md hover:border-border/80 flex flex-col justify-between py-5">
                  <CardContent className="px-5 flex flex-col justify-between h-full gap-4">
                    {/* Top Row: Icon + Meta */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-lg bg-background border border-border/80 text-foreground group-hover:border-accent/40 transition-colors">
                          {renderTopicIcon(topic.iconName)}
                        </div>
                        <span className="text-xs font-semibold tracking-wider uppercase text-muted-foreground">
                          {topic.category}
                        </span>
                      </div>
                      <div className="p-1.5 rounded-full text-muted-foreground group-hover:text-foreground transition-colors">
                        <ArrowUpRight className="size-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                      </div>
                    </div>

                    {/* Middle: Title & Description */}
                    <div className="space-y-1.5">
                      <h2 className="font-serif text-xl sm:text-2xl font-semibold text-card-foreground group-hover:text-primary transition-colors">
                        {topic.title}
                      </h2>
                      <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                        {topic.description}
                      </p>
                    </div>

                    {/* Footer Row: Article / Guide count */}
                    <div className="pt-2 border-t border-border/50 flex items-center justify-between text-xs text-muted-foreground">
                      <span>{topic.articleCount}</span>
                      <span className="font-medium group-hover:text-foreground transition-colors">
                        Explore hub
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>)}
        </motion.div>

        {/* Clinical Governance Footnote Callout */}
        <div className="mt-12 md:mt-16 p-5 sm:p-6 rounded-xl border border-border bg-muted/40 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h3 className="font-serif text-base sm:text-lg font-semibold text-foreground">
              Need immediate veterinary advice?
            </h3>
            <p className="text-sm text-muted-foreground">
              Our clinical library offers symptom assessments, preventive schedules, and urgent care protocols.
            </p>
          </div>
          <Button asChild variant="outline" className="shrink-0 font-medium">
            <Link to="/PetHealthPage">Browse Health Library</Link>
          </Button>
        </div>
      </div>
    </section>;
}